import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PremiumAccessProvider } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import {
  Company,
  CompanyInitialState,
  Contact,
  ContactInitialState,
} from '@hellodarwin/core/lib/features/entities';
import {
  Profile,
  ProfileInitialState,
} from '@hellodarwin/core/lib/features/entities/profile-entities';
import { useAnalytics } from '@hellodarwin/core/lib/features/providers/analytics-provider';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useLogin } from '@hellodarwin/app-core/lib/providers/login-provider';
import { useParams } from '@hellodarwin/core/lib/features/providers/pagination-provider/params-provider';

import { useAppDispatch, useAppSelector } from '../../app/app-hooks';
import InvitationErrorScreen from '../../auth0/invitation-error-screen';
import {
  isStaleInvitationError,
  withoutInvitationToken,
} from '../../auth0/invitation-error-screen/errors';
import { LocalStorageKeys } from '../../local-storage';
import { selectClientPages } from '../../pages/select-client-pages';
import { useClientAnalytics } from '../analytics/use-client-analytics';
import {
  fetchProfile,
  me,
  selectCompany,
  selectContact,
  selectProfile,
  setCheckoutLoading,
} from '../api/slices/profile-slice';
import { useClientApi } from '../api/use-client-api';
import { DevToolRender } from './dependency-provider';

export type OnboardingType = 'subscription' | 'default' | 'assistant' | 'none';
export type FetchClientHandler = (
  checkoutProcessSuccessful?: boolean,
) => Promise<Profile>;
export interface UserContextType {
  profile: Profile;
  isLoggedIn: boolean;
  company: Company;
  contact: Contact;
  onboarding: OnboardingType;
  setOnboarding: React.Dispatch<React.SetStateAction<OnboardingType>>;
}

interface UserProviderProps extends PropsWithChildren {}

const createUserContextDefaultValue = (): UserContextType => ({
  profile: ProfileInitialState,
  isLoggedIn: false,
  company: CompanyInitialState,
  contact: ContactInitialState,
  onboarding: 'none',
  setOnboarding: () => {
    throw new Error(
      'UserProvider: setOnboarding called outside of provider context',
    );
  },
});

export const UserContext = React.createContext<UserContextType>(
  createUserContextDefaultValue(),
);
UserContext.displayName = 'UserContext';

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const api = useClientApi();
  const dispatch = useAppDispatch();

  const { journeyData, loaded } = useAnalytics();
  const analytics = useClientAnalytics();
  const { params, removeParam } = useParams();
  const { accessToken } = useLogin();
  const { changeLanguage, selectedLocale } = useLocale();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const profile = useAppSelector(selectProfile);
  const company = useAppSelector(selectCompany);
  const contact = useAppSelector(selectContact);
  const selectedPages = useMemo(
    () => selectClientPages(profile?.access_type),
    [profile?.access_type],
  );

  const isLoggedIn = useMemo(
    () => !!profile.company_id?.length,
    [profile.company_id],
  );

  function wait(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Initialize onboarding state from localStorage
  const [onboarding, setOnboarding] = useState<OnboardingType>(() => {
    const stored = localStorage.getItem(LocalStorageKeys.ONBOARDING);
    return (stored as OnboardingType) || 'none';
  });

  const onboardingLoaded = useRef(false);
  const [invitationError, setInvitationError] = useState<number | null>(null);

  // fetch client data when access token changes
  useEffect(() => {
    if (accessToken === '' || !loaded) {
      return;
    }

    const invitationToken = new URLSearchParams(window.location.search).get(
      'invitation_token',
    );
    let cancelled = false;
    setInvitationError(null);
    dispatch(
      me({
        api,
        utm: journeyData,
        invitationToken: invitationToken || undefined,
      }),
    ).then((action) => {
      if (
        !cancelled &&
        invitationToken &&
        me.rejected.match(action) &&
        isStaleInvitationError(action.payload?.error_code)
      ) {
        setInvitationError(action.payload.error_code);
      }
    });
    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, loaded]);

  // Handle post-login actions
  useEffect(() => {
    if (!isLoggedIn) return;

    // Set user's preferred language
    if (
      !!profile.contact_preferred_language &&
      profile.contact_preferred_language !== selectedLocale
    ) {
      changeLanguage(profile.contact_preferred_language);
    }
  }, [
    isLoggedIn,
    profile.contact_preferred_language,
    pathname,
    changeLanguage,
    navigate,
    selectedLocale,
  ]);

  // Initialize HubSpot tracking
  useEffect(() => {
    if (!profile?.contact_email) return;

    try {
      const _hsq = (window._hsq = window._hsq || []);
      _hsq?.push([
        'identify',
        {
          email: profile.contact_email,
        },
      ]);
      analytics.identify();
    } catch (error) {
      console.error('Failed to initialize HubSpot tracking:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.contact_email]);

  // Helper function to get onboarding type from localStorage
  const getStoredOnboardingType = useCallback((): OnboardingType => {
    const stored = localStorage.getItem(LocalStorageKeys.ONBOARDING);
    return stored as OnboardingType;
  }, []);

  // Handle checkout return process
  const handleCheckoutReturn = useCallback(async () => {
    const storedType = getStoredOnboardingType();

    if (storedType === 'subscription') {
      setOnboarding('default');
    }
    dispatch(setCheckoutLoading(true));
    await wait(4000);

    await dispatch(
      fetchProfile({
        api,
      }),
    ).unwrap();

    removeParam('from');
    dispatch(setCheckoutLoading(false));
  }, [getStoredOnboardingType, setOnboarding, dispatch, api, removeParam]);

  // Derive onboarding state from the DB (company_onboarded_at) once profile loads.
  // This is the source of truth — works across browsers, devices, and logout cycles.
  useEffect(() => {
    if (onboardingLoaded.current) return;
    if (!isLoggedIn) return;

    onboardingLoaded.current = true;

    // Company already onboarded or non-standard Client shell → skip onboarding
    if (
      profile?.company_onboarded_at ||
      profile?.access_type === 'custom' ||
      profile?.access_type === 'expert'
    ) {
      localStorage.setItem(LocalStorageKeys.ONBOARDING, 'none');
      setOnboarding('none');
      return;
    }

    // Company not onboarded → show onboarding flow.
    // Use localStorage hint for flow type (subscription vs default) if present.
    const storedType = getStoredOnboardingType();
    const flowType = storedType === 'subscription' ? 'subscription' : 'default';
    setOnboarding(flowType);
  }, [
    getStoredOnboardingType,
    profile?.company_onboarded_at,
    profile?.access_type,
    isLoggedIn,
  ]);

  // Sync onboarding state to localStorage
  useEffect(() => {
    if (onboarding !== 'none') {
      localStorage.setItem(LocalStorageKeys.ONBOARDING, onboarding);
    }
  }, [onboarding]);

  // Handle URL parameters for checkout and pricing
  useEffect(() => {
    if (!isLoggedIn || !params || profile?.access_type === 'expert') return;

    // Handle return from checkout
    if (params['from'] === 'checkout') {
      handleCheckoutReturn();
    }

    // Handle advanced pricing selection
    if (params['content_cta'] === 'pricing-advanced') {
      if (onboarding === 'none') {
        removeParam('content_cta');
        navigate('/profile/plans');
      } else {
        setOnboarding('subscription');
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, journeyData, isLoggedIn, profile?.access_type]);

  const handleCheckout = useCallback(async () => {
    if (profile?.access_type === 'expert') return;
    navigate('/profile/plans');
  }, [navigate, profile?.access_type]);

  const contextValue: UserContextType = useMemo(
    () => ({
      profile,
      isLoggedIn,
      company: company ?? CompanyInitialState,
      contact: contact ?? ContactInitialState,
      onboarding,
      setOnboarding,
    }),
    [profile, isLoggedIn, company, contact, onboarding],
  );

  if (invitationError !== null) {
    return (
      <InvitationErrorScreen
        errorCode={invitationError}
        onContinue={() => {
          window.location.replace(withoutInvitationToken(window.location.href));
        }}
      />
    );
  }

  return (
    <UserContext.Provider value={contextValue}>
      <PremiumAccessProvider
        handleCheckout={handleCheckout}
        pages={selectedPages}
        accessLevel={profile?.access_type ?? 'free'}
      >
        <DevToolRender type="DEVELOPER_PROVIDER">{children}</DevToolRender>
      </PremiumAccessProvider>
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export default UserProvider;
